<template>
	<div>
		<!-- delete dialog -->
		<bee-delete-dialog
			v-model="deleteDialog"
			moduleType="filters"
			:ids="deleteItemId"
			@on-delete="fetchFilters"
		>
			{{$t('are-you-sure-you-want-to')}} 
			<span class="text-uppercase error--text">{{$t('delete')}}</span> 
			{{$t('Filters.the-filter')}}
		</bee-delete-dialog>

		<!-- restore dialog -->
		<bee-restore-dialog
			v-model="restoreDialog"
			moduleType="filters"
			:ids="restoreItemId"
			@on-restore="fetchFilters"
		>
			{{$t('are-you-sure-you-want-to')}} 
			<span class="text-uppercase orange--text text-darken-1">{{$t('restore')}}</span> 
			{{$t('Filters.the-filter')}}
		</bee-restore-dialog>

		<!-- add/edit dialog -->
		<v-dialog
			v-model="dialog"
			:width="useDescriptionFor.categories && useUploadFilterImage ? 500 : 400"
			:retain-focus="false"
			:persistent="dialogLoading"
			attach
		>
			<v-form ref="form" @submit.prevent="submit" :disabled="dialogLoading">
				<v-card class="rounded-medium">
					<v-card-title
						class="justify-center text-uppercase text-h6"
						:class="{ 'mb-3': !multiLangInputs, 'pb-2': multiLangInputs }"
					>
						{{ editItemId ? $t('edit') : $t('add')}} {{$t('Filters.filter')}}
					</v-card-title>
					<v-card-text class="pb-2">
						<!-- lang tabs -->
						<v-lang-tabs
							v-if="multiLangInputs"
							v-model="lang"
							:checked-langs="acceptedToSaveLangs"
							:disable-non-selected="loading || dialogLoading"
							background-color="bgColor"
							class="mb-4"
							@change="updateFieldsFromDraft"
						/>

						<!-- inputs -->
						<v-row class="flex-column mt-0">
							<!-- name -->
							<v-col class="pt-0">
								<v-text-field
									v-model="name"
									:label="$t('inputs.name')"
									outlined
									dense
									class="rounded-small"
									hide-details
									:rules="rules.required"
								></v-text-field>
							</v-col>

							<!-- parent filters -->
							<v-col v-if="useFilterTree" class="pt-0">
								<v-autocomplete
									v-model="parentFilterId"
									:items="filters.filter(c => !c.deletedAt)"
									item-text="name"
									item-value="id"
									:label="$t('inputs.parent-filter')"
									outlined
									dense
									hide-details
									class="rounded-small"
								></v-autocomplete>
							</v-col>

							<!-- image -->
							<v-col v-if="useUploadFilterImage" class="pt-0">
								<bee-drop-img
									v-model="image"
									dashed
									border-width="2"
									:border-color="isSubmitted & !image ? '#f00' : '#ccc'"
									radius="11"
									height="180px"
									contain
									blurable
									clearable
									disable-file-picker
									:disabled="dialogLoading"
									@click="$eventBus.$emit('file-manager', { images: true }, (url, file) => {
										image = url;
										fileManagerFiles.mainImage = file;
									})"
								>
									<template v-slot:drag-text>
										<span class="text-body-1 font-weight-medium">
											{{$t('drag-text')}}
										</span>
										<span class="text-caption mb-0">
											{{$t('supported-text')}}
										</span>
									</template>
								</bee-drop-img>
							</v-col>

							<!-- description -->
							<v-col v-if="useDescriptionFor.categories" cols="12" class="pt-0">
								<span class="text-body-1 text-capitalize font-weight-bold">
									{{$t('inputs.description')}}
								</span>
								<text-editor
									v-model="description"
									hide-menubar
									mini-tools
									:disabled="sendLoading"
									:height="200"
									:error="isSubmitted && !description"
								></text-editor>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn
							type="submiy"
							color="primary"
							class="rounded-small"
							:loading="dialogLoading"
							:disabled="dialogLoading"
						>
							{{$t('save')}}
						</v-btn>
						<v-btn
							@click="dialog = false"
							class="rounded-small"
							:disabled="dialogLoading"
						>
							{{$t('cancel')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- filters -->
		<v-card class="rounded-medium mb-3">
			<v-card-text class="pa-2">
				<v-row class="align-center">
					<!-- add -->
					<v-col md="8" cols="12" class="text-sm-start text-center">
						<v-btn
							dark
							color="darkGreen"
							class="rounded-small"
							@click="dialog = true"
						>
							<v-icon>mdi-plus</v-icon>
							{{$t('Filters.add-new-filter')}}
						</v-btn>
					</v-col>

					<!-- search -->
					<v-col md="4" cols="12" class="d-flex flex-sm-row flex-column align-center text-md-start text-center">
						<v-text-field
							v-model="search"
							:label="$t('search')"
							hide-details
							outlined
							dense
							prepend-inner-icon="mdi-magnify"
							class="rounded-medium align-self-stretch me-sm-6 mb-sm-0 mb-5"
						></v-text-field>
						<v-btn
							:color="isFiltered ? null : 'primary'"
							class="rounded-small"
							@click="isFiltered = !isFiltered"
						>
							{{isFiltered ? $t('cancel') : $t('search')}}
						</v-btn>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>

		<!-- tree -->
		<v-card
			v-if="useFilterTree"
			class="rounded-medium"
		>
			<v-card-title v-if="$vuetify.breakpoint.mdAndUp" class="pt-2 pb-1">
				<v-row class="flex-md-row flex-column align-center ma-0 ms-sm-8 text-body-2 font-weight-bold">
					<!-- name -->
					<v-col lg="8" class="py-2">{{$t('headers.name')}}</v-col>

					<!-- image -->
					<v-col v-if="useUploadFilterImage" lg="2" class="py-2">{{$t('headers.image')}}</v-col>

					<!-- description -->
					<v-col v-if="useDescriptionFor.filters" lg="5" class="py-2">{{$t('headers.description')}}</v-col>

					<!-- actions -->
					<v-col lg="2" class="text-end py-2 pe-7">{{$t('headers.actions')}}</v-col>
				</v-row>
			</v-card-title>
			<v-progress-linear v-if="loading" indeterminate absolute/>
			<v-divider/>
			<v-card-text>
				<v-treeview
					:items="loading ? [] : hierarchyFilters"
					:open="hierarchyFilters.map(c => c.id)"
					transition
					dense
					expand-icon="mdi-chevron-down"
				>
					<template v-slot:label="{ item }">
						<v-row class="flex-md-row flex-column align-center tree-row ma-0">
							<!-- name -->
							<v-col lg="8" class="py-0">
								<span v-if="$vuetify.breakpoint.smAndDown" class="text-body-2 me-1">
									{{$t('headers.name')}}:
								</span>

								<span class="text-body-1 font-weight-medium primary--text">
									{{item.name}}
								</span>
							</v-col>

							<!-- image -->
							<v-col v-if="useUploadFilterImage" lg="2" class="py-0">
								<span v-if="$vuetify.breakpoint.smAndDown" class="text-body-2 me-1">
									{{$t('headers.image')}}:
								</span>

								<span class="text-body-1 font-weight-medium">
									<v-img
										v-if="true"
										:src="item.image.url || require('@/assets/blurred.png')"
										:height="45"
										:width="45"
										lazy-src="@/assets/blurred.png"
										class="rounded-medium"
									/>
									<template v-else-if="0">-</template>
								</span>
							</v-col>

							<!-- description -->
							<v-col v-if="useDescriptionFor.filters" lg="5" class="py-0">
								<span v-if="$vuetify.breakpoint.smAndDown" class="text-body-2 me-1">
									{{$t('headers.summary')}}:
								</span>

								<div
									v-html="item.description || '-'"
									class="text-subtitle-2 main--text font-weight-medium mb-0"
								></div>
							</v-col>

							<!-- actions -->
							<v-col lg="2" class="text-md-end text-center py-0">
								<template v-if="!item.deletedAt">
									<tooltip :text="$t('tooltip.edit')">
										<v-btn
											color="blue darken-4"
											dark
											small
											class="px-1 flex-shrink-1"
											min-width="20px"
											@click="editItemId = item.id; dialog = true;"
										><v-icon>mdi-pencil</v-icon></v-btn>
									</tooltip>

									<tooltip :text="$t('tooltip.delete')">
										<v-btn
											color="red darken-1"
											dark
											small
											class="ms-1 px-1"
											min-width="20px"
											@click="deleteItemId = item.id; deleteDialog = true;"
										>
											<v-icon>mdi-delete</v-icon>
										</v-btn>
									</tooltip>
								</template>
								
								<tooltip v-else :text="$t('tooltip.restore')">
									<v-btn
										color="orange darken-1"
										dark
										small
										class="ms-1 px-1"
										min-width="20px"
										@click="restoreItemId = item.id; restoreDialog = true;"
									>
										<v-icon>mdi-restore</v-icon>
									</v-btn>
								</tooltip>
							</v-col>
						</v-row>
						<v-divider/>
					</template>
				</v-treeview>

				<p v-if="loading" class="text-center mb-0">
					{{$vuetify.lang.translator('dataIterator.loadingText')}}
				</p>
			</v-card-text>
		</v-card>
		
		<!-- table -->
		<v-data-table
			v-else
			:headers="headers"
			:items="loading ? [] : filteredFilters"
			:loading="loading"
			dense
			:items-per-page="15"
			class="elevation-3 rounded-medium"
		>
			<!-- image -->
			<template v-slot:item.image="{ item }">
				<v-img
					:src="item.image ? item.image.url : require('@/assets/placeholder-image.png')"
					:height="45"
					:width="45"
					lazy-src="@/assets/blurred.png"
					transition="scale-transition"
					class="rounded-medium mx-auto"
				/>
			</template>
			
			<!-- description -->
			<template v-slot:item.description="{ item }">
				<div v-html="item.description || '-'"/>
			</template>

			<!-- actions -->
			<template v-slot:item.actions="{ item }">
				<template v-if="!item.deletedAt">
					<tooltip :text="$t('tooltip.edit')">
						<v-btn
							color="blue darken-4"
							dark
							small
							class="me-1 px-1 flex-shrink-1"
							min-width="20px"
							@click="editItemId = item.id; dialog = true;"
						><v-icon>mdi-pencil</v-icon></v-btn>
					</tooltip>
					<tooltip :text="$t('tooltip.delete')">
						<v-btn
							color="red darken-1"
							dark
							small
							class="px-1"
							min-width="20px"
							@click="deleteDialog = true; deleteItemId = item.id"
						>
							<v-icon>mdi-delete</v-icon>
						</v-btn>
					</tooltip>
				</template>
				<tooltip v-else :text="$t('tooltip.restore')">
					<v-btn
						color="orange darken-1"
						dark
						small
						class="px-1"
						min-width="20px"
						@click="restoreItemId = item.id; restoreDialog = true"
					>
						<v-icon>mdi-restore</v-icon>
					</v-btn>
				</tooltip>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import rules from '@/helpers/validation rules'
import { mapGetters, mapState } from 'vuex';
import { defaultLang, langs, multiLangInputs } from '@/configs/translates.config';
import { useDescriptionFor, useFilterTree, useUploadFilterImage } from '@/configs/global.config';
import langsDraft from '@/mixins/langs-draft';

export default {
	name: 'Filters',
	mixins: [langsDraft],

	data: () => ({
		dialog: false,
		deleteDialog: false,

		editItemId: null,
		deleteItemId: null,

		restoreDialog: false,
		restoreItemId: null,

		// filters
		search: null,
		isFiltered: null,

		// configs
		multiLangInputs,
		useDescriptionFor,
		useFilterTree,
		useUploadFilterImage,

		// helpers
		loading: false,
		dialogLoading: false,
		isSubmitted: false,
		lang: defaultLang,
		rules,

		// file manager
		fileManagerFiles: {
			mainImage: {}
		},

		// draft for languages 
		draft: {},
		draftNames: ['name', { name: 'description', value: '' }],

		// data
		name: null,
		description: null,
		parentFilterId: null,
		image: null,
	}),

	watch: {
		dialog(val) {
			if (val) {
				if (this.editItemId) {
					if (multiLangInputs) {
						this.fetchFilter();
					} else {
						const item = this.getFilterById(this.editItemId);
						this.name = item.name;
						this.description = item.description;
						this.parentFilterId = item.parentId;
						this.image = item.image?.url ?? null;
					}
				}
			} else {
				this.$refs.form.reset();
				this.description = '';
				this.resetDraft();
				this.image = null;
				this.isSubmitted = false;
				this.fileManagerFiles.mainImage = {};
				this.editItemId = null;
				this.lang = defaultLang;
			}
		}
	},

	computed: {
		...mapState({
			filters: state => state.filters.filters,
			hierarchyFilters: state => state.filters.hierarchyFilters,
		}),

		...mapGetters({
			getFilterById: 'filters/getFilterById'
		}),

		filteredFilters() {
			return this.filterByName(this.isFiltered, this.filters, this.search);
		},

		headers() {
			const headers = [
				{ text: this.$t('headers.name'), value: 'name', align: 'center', class: 'primary--text', cellClass: 'primary--text' },
				{ text: this.$t('headers.actions'), value: 'actions', align: 'center', sortable: false },
			];

			if (useUploadFilterImage) {
				headers.splice(0, 0, { text: this.$t('headers.image'), value: 'image', align: 'center', sortable: false },)
			}
			if (useDescriptionFor.filters) {
				headers.splice(headers.length - 1, 0, {
					text: this.$t('headers.description'),
					value: 'description',
					align: 'center',
				})
			}

			return headers;
		},

		acceptedToSaveLangs() {
			const acceptedLangs = langs.reduce((res, a) => { res[a.key] = false; return res }, {})
			langs.forEach(lang => {
				if (
					this.draft.name[lang.key] &&
					(!useDescriptionFor.filters || this.draft.description[lang.key])
				) {
					acceptedLangs[lang.key] = true;
				}
			});
			return acceptedLangs;
		}
	},

	methods: {
		async submit() {
			this.isSubmitted = true;
			const image = useUploadFilterImage ? this.image : true;
			if (this.$refs.form.validate() && image) {
				this.dialogLoading = true;

				const promises = [];
				let itemData = null;

				const submitRequest = (
					isDefaultOne,
					{ name, description, lang },
					id = this.editItemId
				) => {
					const actionName = id ? 'update' : 'create';

					return this.$store.dispatch(`filters/${actionName}`, {
						id: id,
						name: name,
						description: description,
						filterId: this.parentFilterId,
						image:
							isDefaultOne && typeof this.image !== 'string'
								? this.image
								: null,
						imagePath:
							isDefaultOne && typeof this.image === 'string'
								? this.fileManagerFiles.mainImage.publicPath
								: null,
						imageName:
							isDefaultOne && typeof this.image === 'string'
								? this.fileManagerFiles.mainImage.fileName
								: null,
						acceptLanguage: lang
					});
				};

				const filteredLangs = langs.filter(c => multiLangInputs || c.key === defaultLang);
				for (let index = 0; index < filteredLangs.length; index++) {
					const lang = filteredLangs[index].key;

					const name = this.draft.name[lang];
					const description = this.draft.description[lang];

					const dataDueLang = { name, description, lang };

					if (name) {
						if (!itemData) {
							await submitRequest(!itemData, dataDueLang).then(data => { itemData = data });
						} else {
							promises.push(submitRequest(false, dataDueLang, itemData.id));
						}
					}
				}

				Promise.all(promises).then(() => {
					const message =
						this.editItemId
							? this.$t('$-is-updated-successfully', { name: this.$t('Filters.filter') })
							: this.$t('$-is-created-successfully', { name: this.$t('Filters.filter') });
					this.$eventBus.$emit('show-snackbar', false, message);

					this.fetchFilters();
					this.dialog = false;
				}).catch(() => {
					this.editItemId = itemData.id;
				}).finally(() => {
					this.dialogLoading = false;
				})
			}
		},

		fetchFilters() {
			this.loading = true;
			Promise.all([
				this.$store.dispatch('filters/fetchAll'),
				useFilterTree ? this.$store.dispatch('filters/fetchAllAsHierarchy') : null
			]).finally(() => {
				this.loading = false;
			})
		},

		fetchFilter() {
			this.dialogLoading = true;
			const slug = this.getFilterById(this.editItemId).slug;
			
			const promises = [];
			langs.filter(c => multiLangInputs || c.key === defaultLang).forEach((c, index) => {
				promises.push(
					this.$store.dispatch('filters/fetchBySlug', { slug, acceptLanguage: c.key })
						.then(data => {
							if (index === 0) {
								this.parentFilterId = data.parentId;
								this.image = data.image?.url ?? null;
							}
							this.draft.name[c.key] = data.name;
							this.draft.description[c.key] = data.description;
						})
				);
			});
			
			Promise.all(promises).then(() => {
				this.name = this.draft.name[this.lang];
				this.description = this.draft.description[this.lang];
			}).finally(() => {
				this.dialogLoading = false;
			})
		}
	},

	created() {
		this.fetchFilters();
	}
}
</script>

<style>

</style>